/**
 * VS Code Extension — early-access marketing stub
 * ===============================================
 * Public, no-signup. Mockup of "cost-while-you-code" (annotations
 * inline next to your Terraform/CDK/Pulumi resources). Captures
 * waitlist emails to the engineer lane of Sales Signals.
 *
 * Truthful framing — page clearly states "coming soon, here's the
 * waitlist", we never claim it's already on the Marketplace.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Code2, Sparkles, Mail, Loader2, CheckCircle2,
  Zap, Eye, Github, Bell, ShieldCheck, FileCode2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VSCodeExtension = () => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [primaryIac, setPrimaryIac] = useState('terraform');
  const [spend, setSpend] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) {
      toast.error('A work email helps us reach you when it ships');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/vscode-extension/waitlist`, {
        email: email.trim(),
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        primary_iac: primaryIac || undefined,
        monthly_cloud_spend_usd: spend ? Number(spend) : undefined,
      });
      setDone(true);
      toast.success('Welcome — your spot is saved');
    } catch (err) {
      toast.error("Couldn't save your spot just now", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="vscode-extension-page">
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            {/* LEFT — copy */}
            <div>
              <Badge className="mb-4 bg-amber-500/15 text-amber-300 border-amber-500/40 tracking-wider">
                <Sparkles className="w-3 h-3 mr-1" />PRIVATE BETA · WAITLIST OPEN
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4">
                Cost <span className="text-emerald-400">while you code</span>.
              </h1>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-5">
                The CARTIEAI extension shows the monthly $ cost of every cloud resource as a CodeLens, right inside VS Code. No need to leave your editor, run a separate plan, or guess. <span className="text-emerald-300 font-semibold">Limited private beta · seats opening on a rolling basis</span> — free forever for individual engineers and open-source teams.
              </p>
              <ul className="space-y-2 text-sm text-slate-400 mb-6">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /><span><span className="text-white">Inline cost annotations</span> for Terraform, Pulumi and AWS CDK resources</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /><span><span className="text-white">"Why this resource is expensive"</span> hover hints with cheaper alternatives</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /><span><span className="text-white">Diff view</span> — sees your edits and shows the cost delta vs. the file on disk</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /><span><span className="text-white">Zero secrets</span> — calls our public, no-auth pricing API just like the GitHub Action</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /><span><span className="text-white">Free</span> for individual engineers and open-source teams, forever</span></li>
              </ul>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/api/vscode-extension/download"
                  className="inline-flex items-center px-5 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm shadow-lg shadow-emerald-500/20"
                  data-testid="vscode-install-cta"
                  title="Download the .vsix and install via VS Code → Extensions → Install from VSIX"
                >
                  <Code2 className="w-4 h-4 mr-2" />Download .vsix (Beta)
                </a>
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center px-5 py-2.5 rounded-md border border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/10 hover:border-emerald-400/70 font-medium text-sm transition-all"
                  data-testid="vscode-waitlist-cta"
                >
                  Join the beta waitlist
                </button>
                <a
                  href="https://github.com/cartie-ai/cartie-cost-lens"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-5 py-2.5 rounded-md border border-slate-600 bg-slate-800/30 text-slate-200 hover:bg-slate-800/60 font-medium text-sm"
                  data-testid="vscode-github-cta"
                >
                  <Github className="w-4 h-4 mr-2" />Source on GitHub
                </a>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Built end-to-end · <code className="px-1 bg-white/5 rounded">cartie-cost-lens-0.9.0.vsix</code></span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> MIT-licensed</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-4 italic">
                Same pricing engine that powers the public PR-Cost Predictor and the GitHub Actions snippet. Now in your editor.
              </p>
            </div>

            {/* RIGHT — fake IDE mockup */}
            <div className="relative" data-testid="vscode-mockup">
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-500/20 via-transparent to-teal-500/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-xl bg-[#1e1e1e] border border-white/10 shadow-2xl overflow-hidden">
                {/* Tab bar */}
                <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-black/40 bg-[#252526]">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                  </div>
                  <div className="ml-3 flex">
                    <div className="px-3 py-1 text-xs bg-[#1e1e1e] text-slate-200 rounded-t border-t border-l border-r border-black/40">main.tf</div>
                  </div>
                </div>
                {/* Code with cost annotations */}
                <pre className="p-5 text-[12.5px] leading-relaxed font-mono overflow-x-auto"><span className="text-slate-400">  1 </span><span className="text-emerald-400">resource</span> <span className="text-amber-300">"aws_db_instance"</span> <span className="text-amber-300">"analytics"</span> {'{'}{'\n'}<span className="text-slate-400">  2 </span>  instance_class    = <span className="text-amber-300">"db.r5.4xlarge"</span>  <span className="text-emerald-300 italic">// ≈ $1,152/mo</span>{'\n'}<span className="text-slate-400">  3 </span>  allocated_storage = <span className="text-emerald-300">2000</span>             <span className="text-emerald-300 italic">// ≈ $230/mo gp3</span>{'\n'}<span className="text-slate-400">  4 </span>  multi_az          = <span className="text-emerald-300">true</span>             <span className="text-emerald-300 italic">// 2× → ≈ $1,152/mo extra</span>{'\n'}<span className="text-slate-400">  5 </span>  engine            = <span className="text-amber-300">"postgres"</span>{'\n'}<span className="text-slate-400">  6 </span>{'}'}{'\n'}<span className="text-slate-400">  7 </span>{'\n'}<span className="text-slate-400">  8 </span><span className="text-emerald-400">resource</span> <span className="text-amber-300">"aws_instance"</span> <span className="text-amber-300">"api_worker"</span> {'{'}{'\n'}<span className="text-slate-400">  9 </span>  instance_type = <span className="text-amber-300">"c5.4xlarge"</span>      <span className="text-rose-400 italic">// ⚠ $496/mo · was c5.xlarge ($124/mo)</span>{'\n'}<span className="text-slate-400"> 10 </span>  count         = <span className="text-emerald-300">3</span>{'\n'}<span className="text-slate-400"> 11 </span>{'}'}</pre>
                {/* CodeLens */}
                <div className="border-t border-black/40 bg-[#181818] px-5 py-3 flex flex-wrap items-center gap-3 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-300 font-semibold">Total: ≈ $4,022/mo</span></span>
                  <span className="text-slate-400">·</span>
                  <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-rose-400" /><span className="text-rose-300">+$1,116/mo since last save</span></span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-400 italic">CARTIEAI · us-east-1 on-demand</span>
                </div>
              </div>
              {/* Floating chip */}
              <div className="absolute -bottom-3 -right-3 bg-emerald-500/15 backdrop-blur border border-emerald-500/30 rounded-xl px-3 py-1.5 shadow-lg flex items-center gap-1.5">
                <FileCode2 className="w-3.5 h-3.5 text-emerald-300" />
                <span className="text-[11px] text-emerald-200 font-semibold">Mockup — coming Q2 2026</span>
              </div>
            </div>
          </div>

          {/* Honesty card */}
          <Card className="bg-slate-900/60 border-emerald-500/20" data-testid="honesty-card">
            <CardHeader>
              <CardTitle className="text-base text-white flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />Where we are honestly
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-300 grid sm:grid-cols-3 gap-4">
              <div>
                <p className="text-emerald-400 font-semibold mb-1">✓ Live today</p>
                <p>Public Terraform pricing API, GitHub Actions snippet, PR-Cost Predictor — the same calculator the extension will use.</p>
              </div>
              <div>
                <p className="text-emerald-400 font-semibold mb-1">⊙ Building now</p>
                <p>The VS Code language-server integration, hover hints, and Pulumi/CDK parsers. Co-built with our first 10 design partners.</p>
              </div>
              <div>
                <p className="text-slate-400 font-semibold mb-1">— Not yet</p>
                <p>VS Code Marketplace listing, IntelliJ counterpart, Helm/Kustomize support. We'll ship them in that order.</p>
              </div>
            </CardContent>
          </Card>

          {/* Try-now box */}
          <Card className="mt-6 bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30">
            <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  <Github className="h-4 w-4 text-emerald-400" />
                  Want it on every PR today? Use the GitHub Actions snippet.
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Same pricing engine, no waitlist required. 10 lines of YAML, posts the cost delta as a PR comment using your repo's built-in <code className="text-xs bg-white/5 px-1 rounded">GITHUB_TOKEN</code>.
                </p>
              </div>
              <Link to="/pr-cost-predictor">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="cta-pr-cost">
                  <Sparkles className="h-4 w-4 mr-2" />Get the snippet
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Waitlist dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="waitlist-dialog">
          {done ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="h-5 w-5" />Welcome — you're in.
                </DialogTitle>
                <DialogDescription>
                  Thanks for the trust. Our founder will email <strong>{email}</strong> the moment the extension is ready, and may reach out sooner to learn how you'd like the cost annotations to look. The PR-Cost Predictor on the previous page works today if you'd like to use it in the meantime.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setOpen(false)} data-testid="waitlist-close-btn">Sounds good</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submit}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Code2 className="h-5 w-5 text-emerald-400" />Save my spot
                </DialogTitle>
                <DialogDescription>
                  Tell us a little so the first build reflects how your team actually works. We'll only email you when there's something real to try — never marketing.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-300">Work email *</label>
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourcompany.com" autoFocus data-testid="email-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-300">Your name</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-300">Company</label>
                    <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" data-testid="company-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-300">Primary IaC</label>
                    <select
                      value={primaryIac}
                      onChange={(e) => setPrimaryIac(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                      data-testid="iac-input"
                    >
                      <option value="terraform">Terraform</option>
                      <option value="pulumi">Pulumi</option>
                      <option value="cdk">AWS CDK</option>
                      <option value="cloudformation">CloudFormation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-300">Monthly cloud spend (USD) — optional</label>
                    <Input type="number" min={0} value={spend} onChange={(e) => setSpend(e.target.value)} placeholder="e.g. 50000" data-testid="spend-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="cancel-btn">Maybe later</Button>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" data-testid="submit-btn">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Mail className="h-4 w-4 mr-2" />Save my spot</>}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <footer className="py-6 bg-slate-950 border-t border-white/5 text-center text-xs text-slate-400">
        © 2026 CARTIEAI · <Link to="/privacy-policy" className="hover:text-slate-300">Privacy</Link> · <Link to="/terms" className="hover:text-slate-300">Terms</Link>
      </footer>
    </div>
  );
};

export default VSCodeExtension;
